/**
 * Simplified Heath-Carter somatotype calculator.
 * Uses circumferences and ratios — no skinfold/bone-diameter required.
 * Returns the classic 3-component score (endo-meso-ecto), each on a 1–7 scale.
 */

export type Gender = "male" | "female";
export type FrameSize = "small" | "medium" | "large";

export type SomatotypeKey =
  | "ectomorph"
  | "mesomorph"
  | "endomorph"
  | "ecto_mesomorph"
  | "meso_ectomorph"
  | "meso_endomorph"
  | "endo_mesomorph"
  | "ecto_endomorph"
  | "endo_ectomorph"
  | "central";

export interface BodyTypeInput {
  gender: Gender;
  age: number;
  heightCm: number;
  weightKg: number;
  wristCm: number;
  ankleCm: number;
  hipCm: number;
  waistCm: number;
}

export interface BodyTypeResult {
  endo: number; // 1–7
  meso: number; // 1–7
  ecto: number; // 1–7
  dominant: "endo" | "meso" | "ecto";
  type: SomatotypeKey;
  frame: FrameSize;
  bmi: number;
  hwr: number; // height / weight^(1/3)
  whtr: number; // waist / height
}

/* -------- Frame size from wrist -------- */

export function frameSize(gender: Gender, wristCm: number): FrameSize {
  if (gender === "male") {
    if (wristCm < 17) return "small";
    if (wristCm <= 19) return "medium";
    return "large";
  }
  if (wristCm < 14) return "small";
  if (wristCm <= 16) return "medium";
  return "large";
}

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

/* -------- Component scores -------- */

/** Endomorphy: from BMI and waist-to-height ratio. */
function endomorphy(bmi: number, whtr: number, gender: Gender): number {
  // Anchor: BMI 18 -> ~1.5, BMI 25 -> ~3.5, BMI 30 -> ~5.5, BMI 35+ -> 6.5
  const bmiScore = (bmi - 16) * 0.45;
  // WHtR: 0.4 -> 1, 0.5 -> 3, 0.6 -> 5
  const whtrScore = (whtr - 0.35) * 20;
  // Slight female adjustment (women carry more essential fat).
  const sexAdj = gender === "female" ? 0.5 : 0;
  return clamp(bmiScore * 0.5 + whtrScore * 0.5 + sexAdj, 1, 7);
}

/** Ectomorphy: classic Heath-Carter HWR formula. */
function ectomorphy(hwr: number): number {
  let v: number;
  if (hwr >= 40.75) v = 0.732 * hwr - 28.58;
  else if (hwr > 38.25) v = 0.463 * hwr - 17.63;
  else v = 0.1;
  return clamp(v, 0.5, 7);
}

/**
 * Mesomorphy: muscularity proxy from wrist + ankle (frame) and
 * "corrected girths" of waist + hip relative to height.
 * Higher values mean more muscle mass for one's frame.
 */
function mesomorphy(input: BodyTypeInput): number {
  const { gender, heightCm, wristCm, ankleCm, hipCm, waistCm } = input;
  // Frame index — bigger bones = higher meso baseline.
  const frameIdx = (wristCm + ankleCm) / heightCm; // typical ~0.18–0.23
  // Girth index — larger waist+hip relative to height suggests muscle bulk
  // (also picks up fat — that's why we subtract some endomorphy contribution
  // separately below).
  const girthIdx = (hipCm + waistCm) / heightCm;
  // Anchor scoring (calibrated against typical reference physiques)
  // Calibrated so a typical adult lands near 3–4; athletic build approaches 5–6.
  const frameScore = (frameIdx - 0.20) * 40; // 0.20 -> 0, 0.235 -> ~1.4
  const girthScore = (girthIdx - 1.0) * 4;   // 1.0  -> 0, 1.15  -> 0.6
  const sexAdj = gender === "male" ? 0.3 : 0;
  return clamp(frameScore + girthScore + sexAdj + 2.5, 1, 7);
}

/* -------- Type classification -------- */

function dominantOf(e: number, m: number, c: number): "endo" | "meso" | "ecto" {
  if (e >= m && e >= c) return "endo";
  if (m >= e && m >= c) return "meso";
  return "ecto";
}

/** Heath-Carter style classification: dominant + secondary if within 0.5 pts. */
function classify(endo: number, meso: number, ecto: number): SomatotypeKey {
  const arr = [
    { k: "endo" as const, v: endo },
    { k: "meso" as const, v: meso },
    { k: "ecto" as const, v: ecto },
  ].sort((a, b) => b.v - a.v);
  const [d, s, t] = arr;
  // Central: all three within 1 point of each other and 3–5 range.
  if (d.v - t.v <= 1 && d.v >= 3 && d.v <= 5) return "central";
  // Pure if dominant is 1.5+ above next.
  if (d.v - s.v >= 1.5) {
    if (d.k === "endo") return "endomorph";
    if (d.k === "meso") return "mesomorph";
    return "ectomorph";
  }
  // Mixed — name as "<secondary>-<dominant>morph" (Heath-Carter convention)
  const key = `${s.k}_${d.k}morph` as SomatotypeKey;
  // Map to canonical keys
  const map: Record<string, SomatotypeKey> = {
    meso_endomorph: "meso_endomorph",
    ecto_endomorph: "ecto_endomorph",
    endo_mesomorph: "endo_mesomorph",
    ecto_mesomorph: "ecto_mesomorph",
    endo_ectomorph: "endo_ectomorph",
    meso_ectomorph: "meso_ectomorph",
  };
  return map[key] ?? (d.k === "endo" ? "endomorph" : d.k === "meso" ? "mesomorph" : "ectomorph");
}

/* -------- Main compute -------- */

export function computeBodyType(input: BodyTypeInput): BodyTypeResult {
  const { gender, heightCm, weightKg, wristCm, waistCm } = input;
  const m = heightCm / 100;
  const bmi = weightKg / (m * m);
  const whtr = waistCm / heightCm;
  const hwr = heightCm / Math.cbrt(weightKg);

  const endo = +endomorphy(bmi, whtr, gender).toFixed(1);
  const meso = +mesomorphy(input).toFixed(1);
  const ecto = +ectomorphy(hwr).toFixed(1);

  return {
    endo,
    meso,
    ecto,
    dominant: dominantOf(endo, meso, ecto),
    type: classify(endo, meso, ecto),
    frame: frameSize(gender, wristCm),
    bmi: +bmi.toFixed(1),
    hwr: +hwr.toFixed(2),
    whtr: +whtr.toFixed(2),
  };
}

/* -------- Triangle plot coordinates -------- */

/**
 * Convert a 3-component somatotype to (x, y) on the classic
 * somatochart. Returns coords in a normalized [-3, 3] x [-1, 1.5] frame.
 *  x = ecto − endo
 *  y = 2*meso − (endo + ecto)
 */
export function somatoXY(r: { endo: number; meso: number; ecto: number }): { x: number; y: number } {
  return { x: r.ecto - r.endo, y: 2 * r.meso - (r.endo + r.ecto) };
}

/* -------- Validation -------- */

export type BodyTypeError =
  | "missing"
  | "age_invalid"
  | "height_invalid"
  | "weight_invalid"
  | "wrist_invalid"
  | "ankle_invalid"
  | "hip_invalid"
  | "waist_invalid";

export function validate(inp: Partial<BodyTypeInput>):
  | { ok: true }
  | { ok: false; error: BodyTypeError } {
  const fields = ["age", "heightCm", "weightKg", "wristCm", "ankleCm", "hipCm", "waistCm"] as const;
  for (const f of fields) {
    if (inp[f] == null || !Number.isFinite(inp[f] as number)) return { ok: false, error: "missing" };
  }
  const { age, heightCm, weightKg, wristCm, ankleCm, hipCm, waistCm } = inp as BodyTypeInput;
  if (age < 15 || age > 100) return { ok: false, error: "age_invalid" };
  if (heightCm < 100 || heightCm > 230) return { ok: false, error: "height_invalid" };
  if (weightKg < 25 || weightKg > 350) return { ok: false, error: "weight_invalid" };
  if (wristCm < 10 || wristCm > 25) return { ok: false, error: "wrist_invalid" };
  if (ankleCm < 15 || ankleCm > 35) return { ok: false, error: "ankle_invalid" };
  if (hipCm < 50 || hipCm > 200) return { ok: false, error: "hip_invalid" };
  if (waistCm < 40 || waistCm > 200) return { ok: false, error: "waist_invalid" };
  return { ok: true };
}
